import { debounce, updateMSTObject } from 'helpers';
import { isObservable } from 'mobx';
import { flow, getParent, getSnapshot, types as t } from 'mobx-state-tree';

import { LoadingState } from 'models/ui';
import { withEnv } from 'models/withEnv';

export const ApplicationSortOrder = t.enumeration('ApplicationOrder', [
  'name-desc',
  'name-asc',
  'date-desc',
  'date-asc',
  'notes-desc',
  'notes-asc',
  'avgvote-desc',
  'avgvote-asc',
  'votes-desc',
  'votes-asc',
]);

const PipelineStageSettings = t
  .model('PipelineStageSettings', {
    stageId: t.identifierNumber,
    sort: t.optional(ApplicationSortOrder, 'date-desc'),
  })
  .actions((self) => ({
    setSort(newSort) {
      self.sort = newSort;
    },
  }));

export const JobSettings = t
  .model('JobSettings', {
    searchStr: '',
    selectedMobileStage: 1,
    pipeline: t.map(PipelineStageSettings),
    state: t.optional(LoadingState, 'init'),
    filters: t.optional(
      t.model('SettingsFilters', {
        conjunction: t.optional(t.enumeration('Conjunction', ['and', 'or']), 'and'),
        appliedFilters: t.array(
          t.model('SettingsFilter', {
            id: t.identifier,
            category: t.string,
            field: '',
            comparer: t.string,
            value: t.optional(
              t.union(t.string, t.number, t.array(t.string), t.array(t.number)),
              '',
            ),
          }),
        ),
      }),
      {},
    ),
    columns: t.map(
      t.model('SettingsColumn', {
        order: t.number,
        visible: t.boolean,
      }),
    ),
  })
  .views(withEnv)
  .views((self) => ({
    get parent() {
      return getParent(self);
    },
  }))
  .actions((self) => ({
    setFilters(filters) {
      const appliedFilters = filters.appliedFilters || [];
      if (isObservable(appliedFilters)) {
        const snapshots = appliedFilters.map((f) => getSnapshot(f));
        self.filters.appliedFilters.replace(snapshots);
      } else {
        self.filters.appliedFilters.replace(appliedFilters);
      }

      self.filters.conjunction = filters.conjunction;
      self.save();
    },
    setColumns(columns) {
      self.columns = columns;
      self.save();
    },
    onSearchUpdate: debounce(() => self.parent.onSettingsUpdate(), 300),
    load: flow(function* () {
      self.state = 'loading';
      const res = yield self.env.api.jobs.loadSettings(self.parent.id);
      if (!res.error) {
        const settings = res.data.settings || {};

        if (!settings.columns) {
          settings.columns = {
            name: { order: 0, visible: true },
            email: { order: 1, visible: true },
            date: { order: 2, visible: true },
            jobStageName: { order: 3, visible: true },
            status: { order: 4, visible: true },
            labels: { order: 5, visible: true },
            numMessages: { order: 6, visible: true },
            numNotes: { order: 7, visible: true },
            numReviews: { order: 8, visible: true },
            averageReviews: { order: 9, visible: true },
          };
        }
        updateMSTObject(self, settings);
      }

      self.parent.jobStages.forEach((stage) => {
        if (!self.pipeline.has(stage.id)) {
          self.pipeline.put({ stageId: stage.id });
        }
      });

      self.state = 'loaded';
    }),
    addStageSettings(stageId) {
      self.pipeline.put({ stageId });
      self.save();
    },
    removeStageSettings(stageId) {
      self.pipeline.delete(stageId);
      self.save();
    },
    setApplicationSort(stageId, sort) {
      if (!self.pipeline.has(stageId)) return;
      const settings = self.pipeline.get(stageId);
      settings.setSort(sort);
      self.save();
    },
    setSearchValue(newValue) {
      if (newValue !== self.searchStr) {
        self.searchStr = newValue;
        self.onSearchUpdate();
      }
    },
    clearSearchValue() {
      if (self.searchStr !== '') {
        self.searchStr = '';
        self.onSearchUpdate();
      }
    },
    save() {
      const settings = self.toJSON();
      const data = {
        filters: settings.filters,
        pipeline: settings.pipeline,
        columns: settings.columns,
      };

      self.env.api.jobs.updateSettings(self.parent.id, data);
    },
    selectMobileStage(stage) {
      self.selectedMobileStage = stage;
    },
  }));
